import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SiteEntity } from '../../../types/site';
import type { ProjectIntent, SiteRagReport } from '../../../types/siteRag';
import { Button } from '../../ui/button';
import { canApplyScreeningRecommendations, screeningIntentChoices } from '../../../lib/intent/screeningIntentMode';
import { deriveScreeningArchitectureRecommendations } from '../../../lib/findings/screeningArchitectureRecommendations';
import { deriveScreeningRecommendationsForPresentation } from '../../../lib/findings/screeningRecommendationsForPresentation';
import type { BtmStrategy } from '../../../lib/stage2/stage2Types';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { buildCallReadyFindingsModel, type BuildCallReadyFindingsOptions } from '../../../lib/findings/callReadyFindings';
import { SiteRecommendedPath, selectCompletedScreeningReport } from './SiteRecommendedPath';

/** Saved advice stays separate; explicit choices use the host canonical draft writer. */
export function IntentScreeningRecommendations({ site, reports, sed, analysesState, evidenceLoading = false,
  evidenceUnavailable = false, decisionStage, onReview, draftIntent, onApplyIntent, btmStrategy,
  onSetStrategy, onChooseDemand, readOnly = false }: {
  site: SiteEntity;
  reports: readonly SiteRagReport[];
  sed?: SiteEvidenceDocument | null;
  analysesState: 'loading' | 'settled';
  evidenceLoading?: boolean;
  evidenceUnavailable?: boolean;
  decisionStage?: BuildCallReadyFindingsOptions['decisionStage'];
  onReview: () => void;
  draftIntent?: ProjectIntent;
  onApplyIntent?: (next: ProjectIntent) => void;
  btmStrategy?: BtmStrategy;
  onSetStrategy?: (value: BtmStrategy) => Promise<void>;
  onChooseDemand?: () => void;
  readOnly?: boolean;
}) {
  // Do not reinterpret a failed or missing current read as absence of constraints.
  // Reports are still selected by the same hold/identity gates as Home and Brief.
  const loading = evidenceLoading || analysesState === 'loading' || site.sedStatus === 'bootstrapping';
  const unavailable = evidenceUnavailable || site.sedStatus === 'failed' || Boolean(site.sedId && (
    !sed || sed.id !== site.sedId || site.sedVersion == null || sed.version !== site.sedVersion
  ));
  const model = useMemo(() => loading || unavailable ? null : buildCallReadyFindingsModel(site, reports, {
    sed: sed ?? null, analysesState, decisionStage, economicContributionEnabled: false,
  }), [site, reports, sed, analysesState, decisionStage, loading, unavailable]);

  const screening = selectCompletedScreeningReport(reports, site.id);
  const advice = useMemo(() => ({
    raw: deriveScreeningArchitectureRecommendations(screening, site.id),
    admitted: deriveScreeningRecommendationsForPresentation(screening, site.id),
  }), [screening, site.id]);
  const choices = useMemo(() => loading || unavailable || !draftIntent ? []
    : screeningIntentChoices(screening, site.id, draftIntent, btmStrategy).filter(choice => {
      // Hide the entire action if any part of the choice it would apply lost
      // source support. Keep the shared adoption transforms unchanged.
      if (choice.id === 'cooling') return (['cooling-strategy', 'cooling-type'] as const)
        .every(id => !advice.raw[id] || Boolean(advice.admitted[id]));
      return Boolean(advice.admitted['btm-architecture']);
    }),
  [loading, unavailable, screening, site.id, draftIntent, btmStrategy, advice]);
  const rangeCanBeReviewed = !loading && !unavailable && canApplyScreeningRecommendations(screening, site.id)
    && Boolean(advice.admitted['dc-power']);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const subject = useRef(0);
  useEffect(() => {
    subject.current += 1;
    setBusy(false); setMessage(null);
    return () => { subject.current += 1; };
  }, [site.id, site._ownerUid, screening?.id]);

  if (!screening) return null;

  return (
    <div className="space-y-3" data-testid="intent-screening-recommendations" aria-label="Screening recommendations for Intent">
      {!loading && unavailable ? (
        <section className="instrument-panel" role="status">
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">Recommended path</h2>
          <p className="mt-2 type-body text-text-secondary">Current site evidence is unavailable. Reload the evidence before reviewing screening recommendations; saved Intent choices remain separate.</p>
        </section>
      ) : (
        <SiteRecommendedPath
          screeningReports={reports}
          siteId={site.id}
          liveSite={site}
          answer={model?.questions.find(answer => answer.id === 'resources')}
          risks={model?.questions.find(answer => answer.id === 'power-cooling-risks')?.risks}
          loading={loading}
          onReview={onReview}
        />
      )}
      {onApplyIntent && !loading && !unavailable && (
        <section className="rounded-lg border border-border-subtle p-3 space-y-3" aria-label="Apply screening choices to Intent">
          <h3 className="type-body font-semibold">Choose what to carry into Intent and Stage 2</h3>
          <p className="type-meta text-text-secondary">Only current screening choices are offered. They fill open choices through the same Intent save path; existing decisions are kept. This does not establish engineering capacity, firm fuel, utility capacity or a final GO verdict.</p>
          {choices.map(choice => (
            <div key={choice.id} className="space-y-1">
              <Button type="button" variant="outline" size="sm"
                disabled={readOnly || busy || (choice.strategy != null && !onSetStrategy)}
                onClick={async () => {
                  if (readOnly || busy) return;
                  const active = subject.current;
                  setBusy(true); setMessage(null);
                  try {
                    if (choice.strategy && onSetStrategy) await onSetStrategy(choice.strategy);
                    else if (choice.nextIntent) onApplyIntent(choice.nextIntent);
                    if (active === subject.current) setMessage('Choice sent to the Intent editor. Check the save status below; remaining unknowns are unchanged.');
                  } catch (error) {
                    if (active === subject.current) setMessage(error instanceof Error ? error.message : 'Could not apply the choice.');
                  } finally { if (active === subject.current) setBusy(false); }
                }}>{choice.label}</Button>
              <p className="type-meta text-text-muted">{choice.qualification}</p>
            </div>
          ))}
          {!choices.length && <p className="type-meta text-text-secondary">No new current, unambiguous architecture choices are available to apply. Review saved outcomes and keep undecided values open.</p>}
          {onChooseDemand && rangeCanBeReviewed && <div className="space-y-1">
            <Button type="button" variant="ghost" size="sm" disabled={readOnly || busy} onClick={onChooseDemand}>Choose a demand from the screening range</Button>
            <p className="type-meta text-text-muted">The range stays conditional. Choose a demand and its IT/facility basis in Intent; no midpoint, ceiling or new phase is applied automatically.</p>
          </div>}
          {message && <p className="type-meta text-text-secondary" role="status">{message}</p>}
        </section>
      )}
      <p className="type-meta text-text-secondary">{onApplyIntent
        ? 'Screening outcomes remain advisory until you explicitly apply a choice and save it. Conditional ranges stay conditional; undecided choices remain undecided.'
        : 'Read-only screening advice. Reviewing a recommendation does not accept it: choose and save any Project Intent values yourself. Undecided choices remain undecided.'}</p>
    </div>
  );
}
